'use client';
import React from 'react';
import Link from 'next/link';
import { Loader2 } from 'lucide-react';
import OfferCard from '@/components/public/OfferCard';
import { PLAN_RANK, useMembershipOffers } from '@/lib/membership/useMembershipOffers';

interface MembershipOffersProps {
  /** 'public' shows Course 1 (starter) too; 'portal' shows only growth + growth_governance and plan badge logic. */
  variant: 'public' | 'portal';
  /** Route from my_membership_plan(); only used in portal. `undefined` = still loading. */
  currentRoute?: string | null;
}

export default function MembershipOffers({ variant, currentRoute }: MembershipOffersProps) {
  const { loading, offers, error } = useMembershipOffers();
  const portal = variant === 'portal';

  const visible = offers.filter((o) => (portal ? o.route !== 'starter' : true));
  const main = visible.filter((o) => o.route === 'growth' || o.route === 'growth_governance');
  const starter = visible.find((o) => o.route === 'starter');
  const others = visible.filter((o) => !['growth', 'growth_governance', 'starter'].includes(o.route));
  const myRank = portal && currentRoute ? PLAN_RANK[currentRoute] ?? 0 : 0;

  if (loading) {
    return (
      <p className="py-12 text-center text-sm text-[#2b1a22]/70">
        <Loader2 size={16} className="mr-2 inline animate-spin" />
        Loading memberships…
      </p>
    );
  }

  if (error) {
    return (
      <p className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">
        We couldn&rsquo;t load the memberships right now. Please refresh the page.
      </p>
    );
  }

  if (visible.length === 0) return null;

  const renderCard = (o: (typeof visible)[number]) => {
    const isCurrent = portal && !!currentRoute && o.route === currentRoute;
    const rank = PLAN_RANK[o.route] ?? 0;
    const hideJoin = portal && myRank > 0 && rank < myRank;
    return (
      <OfferCard
        key={o.route}
        offer={o}
        featured={o.route === 'growth_governance'}
        isCurrent={isCurrent}
        hideJoin={hideJoin}
      />
    );
  };

  return (
    <div>
      {main.length > 0 && <div className="grid grid-cols-1 gap-8 pt-3 md:grid-cols-2">{main.map(renderCard)}</div>}
      {others.length > 0 && <div className="mt-8 grid grid-cols-1 gap-8 md:grid-cols-2">{others.map(renderCard)}</div>}
      {starter && <div className="mx-auto mt-8 max-w-xl">{renderCard(starter)}</div>}
      <p className="mt-6 text-sm text-[#2b1a22]/70">
        Founding rates apply for your initial 12-month membership term.{' '}
        <Link href="/terms-and-conditions" className="font-semibold text-[#581C32] underline underline-offset-2">
          View terms
        </Link>
      </p>
    </div>
  );
}
